/**
 * What the calendars ask the server, and what a choice in them does —
 * `docs/new-request.md` §10.1, §10.9, §16.2, §16.3.
 *
 * The aside asks `GET /requests/calendar` for one person and
 * `GET /requests/compare` for side by side, both keyed by the sheet's id so
 * the drive legs Google answered stay in the session (§10.7). A late answer
 * to an earlier question is dropped; the last view stays on screen while the
 * next one comes, so the strip does not flash empty between two days.
 *
 * **A choice is live on the desktop and pending elsewhere.** Beside the form
 * the When box moves as the block does, so a pick goes straight to the sheet.
 * In iPad portrait the schedule *replaces* the form and *Use this time* puts
 * the time on it; on a phone **Done** does. Until then a pick is only this
 * screen's, and **Back** leaves the form as it was.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { MARGIN_MINUTES, placementOf, type CalendarView, type CompareView, type DayPlan,
  type Placement } from '@rowted/shared'
import { requestsApi } from '../api.js'
import { useRequestSheet, type ChosenTime, type PersonRef } from '../sheet-context.js'

/** The questions both views share, from the sheet's facts. */
function useAsk() {
  const api = useRequestSheet()
  const { facts } = api
  return {
    session: api.requestId,
    lat: facts.place?.lat ?? null,
    lng: facts.place?.lng ?? null,
    minutes: facts.minutes,
    orgUnitId: facts.orgUnitId,
    division: facts.divisionCode,
    segment: facts.segment,
    except: api.book?.appointmentId ?? null,
  }
}

export interface Loaded<T> { view: T | null; loading: boolean; failed: boolean }

/** One person's two weeks and the day shown (§10.1–§10.6). */
export function useCalendar(
  personId: string | null, day: string | null, first: string | null,
): Loaded<CalendarView> {
  const api = useRequestSheet()
  const ask = useAsk()
  const [state, setState] = useState<Loaded<CalendarView>>({ view: null, loading: false, failed: false })
  const key = personId
    ? JSON.stringify([personId, day, first, ask])
    : null
  const remember = useRef(api.rememberPlan)
  remember.current = api.rememberPlan

  useEffect(() => {
    if (!key || !personId) { setState({ view: null, loading: false, failed: false }); return }
    let live = true
    setState((was) => ({ ...was, loading: true, failed: false }))
    requestsApi.calendar({ ...ask, person: personId, day, first })
      .then((view) => {
        if (!live) return
        remember.current(view.person.id, view.day.plan)
        setState({ view, loading: false, failed: false })
      })
      .catch(() => { if (live) setState((was) => ({ ...was, loading: false, failed: true })) })
    return () => { live = false }
    /* The key is the whole question. */
  }, [key])

  return state
}

/** Up to three people on one day (§10.9). */
export function useCompare(
  people: string[], date: string | null, first: string | null,
): Loaded<CompareView> {
  const api = useRequestSheet()
  const ask = useAsk()
  const [state, setState] = useState<Loaded<CompareView>>({ view: null, loading: false, failed: false })
  const key = people.length ? JSON.stringify([people, date, first, ask]) : null
  const remember = useRef(api.rememberPlan)
  remember.current = api.rememberPlan

  useEffect(() => {
    if (!key) { setState({ view: null, loading: false, failed: false }); return }
    let live = true
    setState((was) => ({ ...was, loading: true, failed: false }))
    requestsApi.compare({ ...ask, people, date, first })
      .then((view) => {
        if (!live) return
        for (const column of view.people) remember.current(column.person.id, column.day.plan)
        setState({ view, loading: false, failed: false })
      })
      .catch(() => { if (live) setState((was) => ({ ...was, loading: false, failed: true })) })
    return () => { live = false }
    /* The key is the whole question. */
  }, [key])

  return state
}

/** Where the visit is on this screen: a person, a day and a start. */
export interface Shown { personId: string; date: string; start: number }

export interface Choice {
  /** What the calendars draw as the green block and the selected card. */
  shown: Shown | null
  /** It is this screen's and not on the form yet. */
  pending: boolean
  choose: (person: PersonRef, time: ChosenTime) => void
  /** A drag or *Move to*: the same person, a new time. */
  move: (person: PersonRef, time: ChosenTime) => void
  /** *Use this time*, **Done**: put a pending pick on the form. */
  commit: () => void
}

export function useChoice(mode: 'live' | 'pending'): Choice {
  const api = useRequestSheet()
  const [pending, setPending] = useState<{ person: PersonRef; time: ChosenTime } | null>(null)
  const appointment = api.state.appointment
  const committed: Shown | null = appointment.salespersonId && appointment.date && appointment.start !== null
    ? { personId: appointment.salespersonId, date: appointment.date, start: appointment.start }
    : null

  const choose = useCallback((person: PersonRef, time: ChosenTime) => {
    if (mode === 'live') api.choose(person, time)
    else setPending({ person, time })
  }, [api, mode])

  const move = useCallback((person: PersonRef, time: ChosenTime) => {
    if (mode === 'pending') { setPending({ person, time }); return }
    if (committed && committed.personId === person.id) api.moveTo(time)
    else api.choose(person, time)
  }, [api, mode, committed])

  const commit = useCallback(() => {
    if (pending) api.choose(pending.person, pending.time)
  }, [api, pending])

  const shown = mode === 'pending' && pending
    ? { personId: pending.person.id, date: pending.time.date, start: pending.time.start }
    : committed

  return { shown, pending: mode === 'pending' && pending !== null, choose, move, commit }
}

/** The key *Keep* and *Book it anyway* are remembered under (§10.8, once). */
export const anywayKey = (shown: Shown): string => `${shown.personId}|${shown.date}|${shown.start}`

/** Where the shown visit sits in a plan, when it is this person's on this day. */
export function usePlacement(plan: DayPlan | null, personId: string | null, shown: Shown | null,
  minutes: number): Placement | null {
  return useMemo(() => {
    if (!plan || !shown || !personId || shown.personId !== personId || shown.date !== plan.date) return null
    return placementOf(plan, shown.start, minutes)
  }, [plan, personId, shown, minutes])
}

/** Is a drive leaving less than the margin, or not enough at all? */
export const short = (spare: number): boolean => spare < MARGIN_MINUTES
